import { useEffect } from "react";
import { MODELS, useMultiModel } from "../../../api";
import type { ShowroomEntryListItem } from "../../../api/showroom";
import { useShuffled } from "../../../lib/shuffle";
import { isStudent } from "../../../lib/Roles";
import { ROUTES } from "../../../routes.js";
import { setFeedbackTrigger } from "../../feedback/feedbackTriggerStore";
import type { AnySparkBag } from "../sparkBag";
import { SparkShowroomOverviewWithData } from "./SparkShowroomOverviewWithData";

// How long a visitor has to stay on the overview before it counts as having
// looked at the showroom rather than passed through it.
const FEEDBACK_DWELL_MS = 10000;

/**
 * A spark's showroom, in a Lernreise and in the public demo alike.
 *
 * What differs is only what a demo *hasn't* got: with no workshop there are no
 * entries of its own, no team to upload one, no public showroom to link to and
 * nothing to mark as seen. What is left is the entries other Lernreisen have
 * shared for this spark — the second grid in a workshop, and the whole
 * showroom in the demo.
 */
const SparkShowroomOverview = ({ bag }: { bag: AnySparkBag }) => {
  const { workshop, spark, settings } = bag;

  useEffect(() => {
    // Only a Lernreise has a feedback form to trigger.
    if (!workshop) return;
    const timeout = setTimeout(
      () => setFeedbackTrigger("showroom"),
      FEEDBACK_DWELL_MS,
    );
    return () => clearTimeout(timeout);
  }, [workshop]);

  // Fire the independent fetches in parallel (stable-length array; the null
  // slots are the ones a demo, or a non-student, has no workshop or team for).
  // All-or-nothing: suspends until every entry resolves.
  const [ownEntries, unseenComments, sharedEntries, smallTeam] = useMultiModel([
    workshop ? MODELS.WORKSHOP_SHOWROOM_ENTRIES(workshop.id) : null,
    workshop ? MODELS.COMMENTS_UNSEEN({ workshopId: workshop.id }) : null,
    MODELS.SPARK_SHARED_SHOWROOM_ENTRIES(spark.id),
    workshop && isStudent(workshop.roles)
      ? MODELS.SMALL_TEAM({ workshopId: workshop.id })
      : null,
  ]);

  // Dependent on smallTeam (which may be absent), so it's a conditional fetch in
  // its own slot rather than joining the parallel batch above.
  const [smallTeamShowroomData] = useMultiModel([
    smallTeam ? MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeam.id) : null,
  ]);

  // Without a workshop the shared entries are the showroom itself, not the
  // "other Lernreisen" section under it.
  const entries: ShowroomEntryListItem[] = ownEntries ?? sharedEntries;
  const ownEntryIds = new Set(ownEntries?.map((e) => e.id));
  const otherSharedEntries = ownEntries
    ? sharedEntries.filter((e) => !ownEntryIds.has(e.id))
    : [];
  const sortedEntries = useShuffled(
    entries,
    settings.projectSortMode === "random",
  );

  const unseenByEntryId = (unseenComments ?? []).reduce<Record<string, number>>(
    (m, uc) => {
      m[uc.showroomEntryId] = (m[uc.showroomEntryId] || 0) + 1;
      return m;
    },
    {},
  );

  const uploadEditButtonTarget =
    workshop && smallTeam
      ? `${ROUTES.spark.getUrl({
          workshopSlug: workshop.slug,
          sparkSlug: spark.slug,
        })}/team-diary/${smallTeam.id}`
      : null;

  const publicShowroomButtonTarget = workshop?.publicShowroom
    ? `${import.meta.env.VITE_SHOW_HOST}/room/${workshop.publicShowroom.slug}`
    : null;

  return (
    <SparkShowroomOverviewWithData
      entries={sortedEntries}
      otherSharedEntries={otherSharedEntries}
      unseenByEntryId={unseenByEntryId}
      workshopSettings={settings}
      hasExistingShowroomEntry={Boolean(smallTeamShowroomData?.entry)}
      publicShowroomButtonTarget={publicShowroomButtonTarget}
      uploadEditButtonTarget={uploadEditButtonTarget}
    />
  );
};

export default SparkShowroomOverview;
